import { ControlPosition } from '@xyflow/react';
import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { calculateIsResizingHorizontal } from './helper';

interface SelectiveAspectRatioResizerCtxProps {
  resizingPosition: ControlPosition | null;
  setResizingPosition: (position: ControlPosition | null) => void;
  initialWidthRef: React.MutableRefObject<number | undefined>;
}

export const SelectiveAspectRatioResizerCtx = createContext<SelectiveAspectRatioResizerCtxProps>({
  resizingPosition: null,
  setResizingPosition: () => {},
  initialWidthRef: { current: undefined },
});

export const SelectiveAspectRatioResizerCtxProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [resizingPosition, setResizingPosition] = useState<ControlPosition | null>(null);
  const initialWidthRef = useRef<number | undefined>(undefined);
  return (
    <SelectiveAspectRatioResizerCtx.Provider
      value={useMemo(
        () => ({ resizingPosition, setResizingPosition, initialWidthRef }),
        [resizingPosition, setResizingPosition, initialWidthRef],
      )}
    >
      {children}
    </SelectiveAspectRatioResizerCtx.Provider>
  );
};

export const useSelectiveAspectRatioResizerCtx = () => {
  const context = useContext(SelectiveAspectRatioResizerCtx);
  if (!context) {
    throw new Error(
      'useSelectiveAspectRatioResizerCtx must be used within a SelectiveAspectRatioResizerCtxProvider',
    );
  }
  const isResizing = context.resizingPosition !== null;
  const isResizingHorizontal = calculateIsResizingHorizontal(context.resizingPosition);

  return useMemo(
    () => ({
      isResizing,
      isResizingHorizontal,
      setResizingPosition: context.setResizingPosition,
      initialWidthRef: context.initialWidthRef,
    }),
    [isResizing, isResizingHorizontal, context.setResizingPosition, context.initialWidthRef],
  );
};
